import { useEffect, useState } from 'react';

import { apiService, CampaignDto } from '@shared/services/apiService/apiService';
import { knowledgeBaseApi } from '@shared/services/apiService/knowledgeBaseApi';
import {
  acquireRealtimeSocket,
  type RealtimeSocketHandle,
} from '@shared/services/realtimeSocketManager';
import { useAppDispatch } from '@shared/store';
import type { CampaignFilesChangedMessage } from '@features/Admin/KnowledgeBase/model/types';

interface UseCampaignRealtimeSyncOptions {
  campaignId?: string;
  companyId?: string;
}

export const useCampaignRealtimeSync = ({
  campaignId,
  companyId,
}: UseCampaignRealtimeSyncOptions) => {
  const dispatch = useAppDispatch();
  const [realtime, setRealtime] = useState<RealtimeSocketHandle | null>(null);

  useEffect(() => {
    const handle = acquireRealtimeSocket(import.meta.env.VITE_API_URL, '/campaigns');
    setRealtime(handle);

    return () => {
      handle.release();
      setRealtime(null);
    };
  }, []);

  useEffect(() => {
    if (!realtime || !companyId) return;

    return realtime.joinRoom('subscribe_to_get_campaigns_list', { companyId });
  }, [realtime, companyId]);

  useEffect(() => {
    if (!realtime || !campaignId) return;

    // Pushes missed while disconnected are not replayed; refetch the files on every (re)connect
    const refreshCampaignFiles = () => {
      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
    };

    const leaveRoom = realtime.joinRoom('subscribe_to_get_campaign_files', { campaignId });
    const offConnect = realtime.on('connect', refreshCampaignFiles);

    return () => {
      offConnect();
      leaveRoom();
    };
  }, [realtime, campaignId, dispatch]);

  useEffect(() => {
    if (!realtime || !campaignId) return;

    const onCampaignUpdate = (value: { result: CampaignDto }) => {
      const updatedCampaign = value?.result;
      if (!updatedCampaign || updatedCampaign.id !== campaignId) return;

      dispatch(
        apiService.util.updateQueryData(
          'campaignControllerCampaign',
          { id: campaignId },
          (draft) => {
            if (draft) {
              draft.status = updatedCampaign.status;
            }
          },
        ),
      );
    };

    const unsubscribers = [
      realtime.on('get_campaigns_list', onCampaignUpdate),
      // The campaign status arrives by push too
      realtime.onMissedPushes(() =>
        dispatch(apiService.util.invalidateTags([{ type: 'Campaign', id: campaignId }])),
      ),
    ];

    return () => unsubscribers.forEach((off) => off());
  }, [realtime, campaignId, dispatch]);

  useEffect(() => {
    if (!realtime || !campaignId) return;

    const onCampaignFilesChanged = (value: CampaignFilesChangedMessage) => {
      if (value?.result?.campaignId !== campaignId) return;

      dispatch(knowledgeBaseApi.util.invalidateTags([{ type: 'CampaignFiles', id: campaignId }]));
    };

    return realtime.on('get_campaign_files', onCampaignFilesChanged);
  }, [realtime, campaignId, dispatch]);
};
